import Logo from '@/components/Logo.js';
import { SnackbarProvider } from '@/context/SnackbarContext.js';
import { deleteAccountAfterValidationFailure, useFirebaseAuth } from '@/utils/firebase.js';
import { initDevTools } from '@/utils/inits.js';
import { trpc } from '@/utils/trpc.js';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { Card } from '@repo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from '../context/SnackbarContext.js';
import EmailVerification from '../pages/auth/signup/EmailVerification.js';
import { useCommonCurrentUser, useCurrentStaffUser } from '../utils/api.js';
import MagicLoading from './MagicLoading.js';

/**
 * Layout for staff authentication pages (login, signup, setup)
 * Handles auth routing based on staff user state and setup completion
 */
const SchoolAuthLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: firebaseUser, isInitialized } = useFirebaseAuth();
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  // Base auth paths that are allowed without authentication
  const baseAuthPaths = useMemo(() => [
    '/school/auth/login',
    '/school/auth/signup',
  ], []);

  // Use common endpoint for routing decisions
  const { data: commonUser, isLoading: isCommonLoading, isError: isCommonError } = useCommonCurrentUser({
    enabled: isInitialized && !!firebaseUser,
    showErrorSnackbar: false
  });

  // Use staff-specific endpoint only when we know user is staff
  const { data: user, isLoading: isStaffLoading, isError: isStaffError, error } = useCurrentStaffUser({
    enabled: isInitialized && !!firebaseUser && commonUser?.data?.userType === 'staff',
    showErrorSnackbar: false
  });

  const isLoading = isCommonLoading || (commonUser?.data?.userType === 'staff' && isStaffLoading);
  const isError = isCommonError || (commonUser?.data?.userType === 'staff' && isStaffError);

  // Safe paths that don't require redirection when authenticated
  const safeAuthPaths = useMemo(() => [
    '/school/auth/login',
    '/school/auth/signup',
    '/school/auth/signup/details',
    '/school/auth/existingUser',
  ], []);

  // Helper function to navigate while preserving URL parameters
  const navigateWithParams = useCallback((path: string) => {
    const searchParams = new URLSearchParams(location.search);
    const fullPath = searchParams.toString() ? `${path}?${searchParams.toString()}` : path;
    navigate(fullPath);
  }, [navigate, location.search]);

  // Handle magic link token persistence
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const magicToken = urlParams.get('magic_link_token');
    if (magicToken) {
      localStorage.setItem('magic_link_token', magicToken);
    }
  }, [location.search]);

  useEffect(() => {
    // Only fetch current user when authenticated
    if (!isInitialized) return;
    const currentPath = location.pathname;

    if (!firebaseUser) {
      if (!baseAuthPaths.some(path => currentPath === path)) {
        navigateWithParams('/school/auth/login');
      }
      return;
    }

    // Check email verification for authenticated users
    if (firebaseUser && !firebaseUser.emailVerified) {
      setShowEmailVerification(true);
      return; // Block all other logic until email is verified
    } else {
      setShowEmailVerification(false);
    }

    if (isLoading) return;

    // Handle user role-based routing
    if (commonUser?.data) {
      if (commonUser.data.cookies?.developerAid) {
        initDevTools();
      }

      const currentUserType = commonUser.data.userType;

      if (currentUserType === 'student') {
        // User is a student trying to access staff area, redirect to convert to staff page
        if (!currentPath.startsWith('/school/auth/existingUser')) {
          navigateWithParams('/school/auth/existingUser');
          return;
        }
      } else if (currentUserType === 'staff') {
        // User is already staff, shouldn't be on convert page
        if (currentPath.startsWith('/school/auth/existingUser')) {
          navigate('/school/app/organization');
          return;
        }

        // Handle staff-specific error (like FORBIDDEN from staff endpoint)
        if (error && error.data?.code === 'FORBIDDEN' && error.message.startsWith('Access denied.')) {
          // Check if it's an email domain error - if so, delete the account
          if (error.message.includes('email') || error.message.includes('domain') || error.message.includes('Personal')) {
            deleteAccountAfterValidationFailure(firebaseUser).then(() => {
              showSnackbar('Account deleted due to invalid email domain. Please use your work or institutional email address.', 'error');
            }).catch(() => {
              showSnackbar('Please use your work or institutional email address to create a staff account.', 'error');
            });
          }
          navigateWithParams('/school/auth/login');
          return;
        }
      } else if (currentUserType === 'unassigned') {
        if (!currentPath.startsWith('/school/auth/signup/details')) {
          navigateWithParams('/school/auth/signup/details');
          return;
        }
      }
    } else if (isCommonError && !baseAuthPaths.some(path => currentPath === path)) {
      navigateWithParams('/school/auth/login');
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const override = searchParams.get('override') === 'true';

    // If authenticated and on a login/signup page, redirect based on setup stage
    if (user) {
      const isAuthPath = safeAuthPaths.some(path => currentPath.startsWith(path));
      // Override only works for the setup step, not for the login/signup page
      if (override && currentPath.startsWith('/school/auth/signup/')) return;
      if (!isAuthPath) return; // Let the user stay on non-auth paths

      // Check if staff profile setup is complete
      const hasStaffProfile = user.data?.staffProfile;

      if (!hasStaffProfile && !currentPath.includes('/school/auth/signup/details')) {
        // No staff profile, go to setup
        navigateWithParams('/school/auth/signup/details');
        return;
      } else if (hasStaffProfile && isAuthPath) {
        // Setup complete, go to app
        navigate('/school/app/organization');
        return;
      }
    }
  }, [user, commonUser, location.pathname, location.search, isLoading, isError, isCommonError, firebaseUser, isInitialized, error, baseAuthPaths, navigateWithParams, navigate, safeAuthPaths, showSnackbar]);



  useEffect(() => {
    if (!commonUser && firebaseUser) {
      queryClient.invalidateQueries({
        queryKey: trpc.commonUser.getCurrentUser.queryKey(),
      })
    }
  }, [commonUser, firebaseUser, queryClient])
  // Show loading while checking auth
  if (isLoading) {
    return (
      <MagicLoading
        active
        onMagic={() => navigate('/school/auth/login')}
      />
    );
  }

  const isBaseAuthPath = baseAuthPaths.some(path => location.pathname.split('?')[0] === path);

  return (
    <SnackbarProvider>
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          py: { xs: 2, md: 4, lg: 8 }
        }}
      >
        <Stack flex={1} direction='row' justifyContent='center'>
          <Logo height={72} />
        </Stack>
        <Box sx={{ maxWidth: 'sm', flex: 1, width: '100%' }}>
          <Card
            sx={{
              p: 4,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
            }}
          >
            <Outlet />
            {isBaseAuthPath && <>
              <Divider sx={{ my: 2, width: '100%' }} />
              <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                <Typography component="span">
                  By creating an account, or logging in, you agree to our {' '}
                </Typography>
                <Typography
                  component="a"
                  href="https://forum.getzube.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'underline', display: 'inline' }}
                >
                  Privacy Policy
                </Typography>
                <Typography component="span">
                  , {' '}
                </Typography>
                <Typography
                  component="a"
                  href="https://forum.getzube.com/privacy/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'underline', display: 'inline' }}
                >
                  Terms of Service
                </Typography>
                <Typography component="span">
                  , and {' '}
                </Typography>
                <Typography
                  component="a"
                  href="https://forum.getzube.com/privacy/cookies"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'underline', display: 'inline' }}
                >
                  Cookie Notice
                </Typography>
                .
              </Typography>
            </>}
          </Card>
        </Box>
        <Box flex={1} />

        {/* Email verification dialog */}
        {firebaseUser && (
          <EmailVerification
            email={firebaseUser.email || ''}
            open={showEmailVerification}
          />
        )}
      </Stack>
    </SnackbarProvider>
  );
};

export default SchoolAuthLayout;